import { Component, Input, Output, EventEmitter, forwardRef, Injector, AfterViewInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors, NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-time-select',
    templateUrl: './time-select.component.html',
    styleUrls: ['./time-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TimeSelectComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TimeSelectComponent),
            multi: true
        }
    ]
})
export class TimeSelectComponent implements ControlValueAccessor, Validator, AfterViewInit {
    @Input() label: string = '';
    @Input() placeholder: string = '--:--';
    @Input() disabled: boolean = false;
    @Input() required: boolean = false;
    @Input() requiredErrorKey: string = 'pages.branches.timeSlotDialog.inputValidation.from.required';
    @Input() invalidErrorKey: string = 'pages.branches.timeSlotDialog.inputValidation.to.invalid';

    @Output() timeChange = new EventEmitter<string>();

    timeValue: string = '';
    private onChange = (value: string) => { };
    private onTouched = () => { };
    private onValidatorChange = () => { };
    private ngControl: NgControl | null = null;

    constructor(private injector: Injector) { }

    ngAfterViewInit() {
        this.ngControl = this.injector.get(NgControl, null);
    }

    get value(): string {
        return this.timeValue;
    }

    set value(val: string) {
        this.timeValue = val;
        this.onChange(val);
        this.onTouched();
    }

    // ControlValueAccessor implementation
    writeValue(value: string): void {
        this.timeValue = value || '';
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onTimePickerChange(): void {
        this.onChange(this.timeValue);
        this.onTouched();
        this.timeChange.emit(this.timeValue);
        this.onValidatorChange();
    }

    clearTime(): void {
        this.timeValue = '';
        this.onChange('');
        this.onTouched();
        this.timeChange.emit('');
        this.onValidatorChange();
    }

    get hasValue(): boolean {
        return !!this.timeValue;
    }

    get displayValue(): string {
        return this.timeValue || this.placeholder;
    }

    // Validator implementation
    validate(control: AbstractControl): ValidationErrors | null {
        // This will be called by Angular's validation system
        return null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    // Error handling methods
    get hasErrors(): boolean {
        return this.ngControl ? !!(this.ngControl.errors && this.ngControl.touched) : false;
    }

    get errorMessages(): string[] {
        if (!this.ngControl || !this.ngControl.errors || !this.ngControl.touched) {
            return [];
        }

        const errors = [];
        if (this.ngControl.errors['required']) {
            errors.push(this.requiredErrorKey);
        }
        if (this.ngControl.errors['invalid']) {
            errors.push(this.invalidErrorKey);
        }

        return errors;
    }
}
